// src/features/locations/LocationsMap.jsx
//
// Interactive MapLibre map for the Portfolio view. Replaces the legacy
// LocationMapView in "Ripple Workplace App.html" (lines 7400-7800) and
// retires DEMO ONLY inventory item 5 — the popup -> React bridge no
// longer leans on `window.__mapNavCallback`. Instead the popup body is
// a real React subtree in a MapLibre popup and the "View Details" button
// calls the `onSelectLocation` prop.
//
// Slice 9 update: PortfolioView now owns the data fetch + filtering and
// passes the visible `locations` set in via props. The internal fetch
// path is preserved as a fallback for the standalone unit tests and
// any direct mounts.
//
// Sort uses rankSites
// for parity with List view; marker + sidebar dot encode SPACE FIT;
// sidebar metadata shows sqft + seats. STAGE_MARKER_COLOR survives
// for the popup body's stage chip only.

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { getLocations } from "../../data/locationsClient.js";
import { ErrorBanner } from "../../components/ErrorBanner.jsx";
import { T, STAGES } from "../../app/tokens.js";
import { S } from "../../app/styles.js";
import { log } from "../../utils/logger.js";
import { rankSites } from "../../utils/rankSites.js";
import { fmtSqft } from "../../utils/format.js";
import { getDisplayedSqft, getAnalyticalSqft, isServicedNoArea } from "../../utils/locationSqftDerived.js";
import {
  computeOfficeSpaceFit,
  SPACE_FIT_HEX,
  SPACE_FIT_SEVERITY,
  SPACE_FIT_LABEL,
  SPACE_FIT_VERDICT_OVER,
  SPACE_FIT_VERDICT_TIGHT,
  SPACE_FIT_VERDICT_UNDER,
  SPACE_FIT_VERDICT_RIGHT,
} from "../../services/officeSeatFitPresentation.js";
import { HowWeDefineThis } from "../../components/SpacePolicyDefinitions.jsx";
import { USABLE_CAPACITY_STAGES } from "../../utils/capacityStages.js";
import { OfficeMapCanvas, effectiveLat, effectiveLng, hasCoords } from "./OfficeMapFoundation.jsx";

// Micro-label shown in the sidebar and popup for non-usable-stage offices.
// Mirrors SPACE_FIT_STAGE_NOTE in OfficesPage.jsx (list) and LocationTile.jsx (tiles).
const SPACE_FIT_STAGE_NOTE = {
  build:   "In build",
  vacated: "Vacated",
};

// Space Fit fallback color — used for locations with no computable verdict
// (missing SF or seats data). Distinct from any verdict color.
const SPACE_FIT_MARKER_FALLBACK = T.mid;

function spaceFitColorFor(loc) {
  // In-Build offices show the stage-blue color, matching the list-view In-Build pill.
  if (loc.stage === "build") return T.buildBlue;
  // CAPACITY-DEFINITION-FIX: vacated/closed offices are not scored — always return
  // the fallback gray regardless of seats/people data, matching OfficesPage list
  // and LocationTile tile views.
  if (!USABLE_CAPACITY_STAGES.includes(loc.stage)) return SPACE_FIT_MARKER_FALLBACK;
  const sf = getAnalyticalSqft(loc);
  const seats = loc.installedCapacity != null ? Number(loc.installedCapacity) : null;
  const people = loc.latestHeadcount?.totalCount != null ? Number(loc.latestHeadcount.totalCount) : null;
  if (import.meta.env.DEV && loc.stage !== "closed") {
    if (seats == null || seats === 0) {
      log.warn(`[map] gray pin - missing installedCapacity for ${loc.id} (stage: ${loc.stage})`);
    } else if (people == null || people === 0) {
      log.warn(`[map] gray pin - missing latestHeadcount for ${loc.id} (stage: ${loc.stage})`);
    }
  }
  const { verdict } = computeOfficeSpaceFit(loc);
  return verdict ? SPACE_FIT_HEX[verdict] : SPACE_FIT_MARKER_FALLBACK;
}

// Compute the Space Fit verdict for a location (same inputs as spaceFitColorFor).
// Returns the verdict string (e.g. "Over-Capacity") or null when data is absent.
// CAPACITY-DEFINITION-FIX: returns null for non-usable stages (build/vacated/closed).
function computedSpaceFitVerdict(loc) {
  if (!USABLE_CAPACITY_STAGES.includes(loc.stage)) return null;
  const { verdict } = computeOfficeSpaceFit(loc);
  return verdict ?? null;
}

// LSE-MAP-CLUSTER-WORST-STATUS-WINS
// Precedence: Over capacity (0) > Dense (1) > Under-used (2) > Right-sized (3).
// Lower severity number = worse. Closed-stage locations are excluded from the
// comparison; if ALL are closed the cluster falls back to SPACE_FIT_MARKER_FALLBACK.
// groupByCoords — groups the `mapped` array by `"${lat},${lng}"` key.
// Returns a Map<string, loc[]>. Groups of length >= 2 are co-located pairs
// that need a ColocatedMarker; singletons get the regular LocationMarker.
export function groupByCoords(locations) {
  const groups = new Map();
  for (const loc of locations) {
    const lat = effectiveLat(loc);
    const lng = effectiveLng(loc);
    const key = `${lat},${lng}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(loc);
  }
  return groups;
}

export function worstClusterColor(locations) {
  const nonClosed = locations.filter((l) => l.stage !== "closed");
  if (nonClosed.length === 0) return SPACE_FIT_MARKER_FALLBACK;

  let worstSeverity = Infinity;
  let worstVerdict = null;
  for (const loc of nonClosed) {
    const v = loc.spaceFitVerdict ?? null;
    if (v != null) {
      const sev = SPACE_FIT_SEVERITY[v] ?? Infinity;
      if (sev < worstSeverity) {
        worstSeverity = sev;
        worstVerdict = v;
      }
    }
  }
  if (worstVerdict) return SPACE_FIT_HEX[worstVerdict];
  // No scored verdict — fall back to stage-based color if any non-closed location is In-Build.
  const hasInBuild = nonClosed.some((l) => l.stage === "build");
  return hasInBuild ? T.buildBlue : SPACE_FIT_MARKER_FALLBACK;
}

function severityForLocations(locations) {
  const verdicts = locations
    .filter((location) => location.stage !== "closed")
    .map(computedSpaceFitVerdict)
    .filter(Boolean)
    .map((verdict) => SPACE_FIT_SEVERITY[verdict] ?? Infinity);
  if (verdicts.length) return Math.min(...verdicts);
  if (locations.some((location) => location.stage === "build")) return 4;
  return 5;
}

const DEFAULT_CENTER = [20, 10];
const DEFAULT_ZOOM = 2;

// ── Map legend ────────────────────────────────────────────────────────────────

const LEGEND_ITEMS = [
  { verdict: SPACE_FIT_VERDICT_OVER,  label: SPACE_FIT_LABEL[SPACE_FIT_VERDICT_OVER].full  },
  { verdict: SPACE_FIT_VERDICT_TIGHT, label: SPACE_FIT_LABEL[SPACE_FIT_VERDICT_TIGHT].full },
  { verdict: SPACE_FIT_VERDICT_UNDER, label: SPACE_FIT_LABEL[SPACE_FIT_VERDICT_UNDER].full },
  { verdict: SPACE_FIT_VERDICT_RIGHT, label: SPACE_FIT_LABEL[SPACE_FIT_VERDICT_RIGHT].full },
  { color: T.buildBlue,              label: "In-Build"                                     },
  { verdict: null,                    label: "No data"                                      },
];

function MapSpaceFitLegend() {
  return (
    <div
      data-testid="map-space-fit-legend"
      style={{
        position: "absolute",
        bottom: 28,
        right: 12,
        zIndex: 1000,
        background: T.card,
        border: `1px solid ${T.border}`,
        borderRadius: 8,
        padding: "8px 12px",
        fontSize: T.fsXs,
        fontWeight: T.fwSemibold,
        boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
        pointerEvents: "auto",
      }}
    >
      <div style={{ fontSize: T.fsXxs, fontWeight: T.fwBold, color: T.light, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
        Space Fit
      </div>
      {LEGEND_ITEMS.map(({ verdict, color, label }) => (
        <div key={label} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
          <span style={{
            display: "inline-block",
            width: 10,
            height: 10,
            borderRadius: "50%",
            background: color ?? (verdict ? SPACE_FIT_HEX[verdict] : SPACE_FIT_MARKER_FALLBACK),
            flexShrink: 0,
          }} />
          <span style={{ color: T.dark }}>{label}</span>
        </div>
      ))}
      <HowWeDefineThis />
    </div>
  );
}

// ── Marker popup content ───────────────────────────────────────────────────────

function LocationPopup({ location, onSelectLocation, markerColor }) {
  const stage = STAGES[location.stage] || { label: location.stage };
  const stageNote = SPACE_FIT_STAGE_NOTE[location.stage] ?? null;

  return (
    <div data-testid={`map-popup-${location.id}`} style={{ width: 240, fontSize: T.fsBase }}>
          {location.imageUrl && (
            <img
              src={location.imageUrl}
              alt=""
              loading="lazy"
              data-testid={`map-popup-thumb-${location.id}`}
              onError={(e) => { e.currentTarget.style.display = "none"; }}
              style={{
                display: "block", width: "100%", height: 140,
                objectFit: "cover", borderRadius: 4, marginBottom: 8,
              }}
            />
          )}
          <div style={{ fontWeight: T.fwBold, color: T.dark, marginBottom: 6 }}>
            {location.market}
            {location.locationAlias && (
              <span style={{ fontWeight: T.fwRegular, color: T.mid, fontSize: T.fsSm, marginLeft: 4 }}>
                · {location.locationAlias}
              </span>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
            <span
              data-testid={`map-popup-health-dot-${location.id}`}
              style={{
                display: "inline-block", width: 8, height: 8, borderRadius: "50%",
                background: markerColor,
              }}
            />
            <span style={{ color: T.mid, fontSize: T.fsSm }}>{stage.label}</span>
            {stageNote && (
              <span
                data-testid={`map-popup-space-fit-note-${location.id}`}
                style={{ fontSize: T.fsXxs5, color: "var(--v2-light)" }}
              >
                {stageNote}
              </span>
            )}
          </div>
          {(() => {
            // MV2: mirror the SidebarRow (lines 189-193) inline pattern so
            // both surfaces stay symmetric. fmtSqft returns "" for null/0;
            // seats string is "" when installedCapacity is null/0. If both
            // are empty, the entire line is omitted (no orphan separator).
            const sqftRaw  = getDisplayedSqft(location, null);
            const sqftStr  = sqftRaw != null
              ? fmtSqft(sqftRaw)
              : (isServicedNoArea(location) ? "n/a" : "");
            const seatsStr = location.installedCapacity
              ? `${Math.round(location.installedCapacity)} seats`
              : "";
            const meta = [sqftStr, seatsStr].filter(Boolean).join(" · ");
            if (!meta) return null;
            return (
              <div
                data-testid={`map-popup-meta-${location.id}`}
                style={{ color: T.mid, fontSize: T.fsSm, marginBottom: 8 }}
              >
                {meta}
              </div>
            );
          })()}
          <button
            type="button"
            onClick={() => onSelectLocation?.(location.id)}
            data-location-id={location.id}
            data-testid={`map-popup-view-${location.id}`}
            style={{
              width: "100%", padding: "6px 0",
              background: T.hdrMid, color: T.white,
              border: "none", borderRadius: 5,
              fontSize: T.fsSm, fontWeight: T.fwSemibold, cursor: "pointer",
            }}
          >
            View Details →
          </button>
    </div>
  );
}

function ColocatedPopup({ locations, onSelectLocation, selectedId }) {
  return (
    <div style={{ minWidth: 210, fontSize: T.fsSm }}>
      {locations.map((loc) => (
        <div
          key={loc.id}
          style={{
            display: "flex", alignItems: "center", gap: 8, padding: "6px 0",
            borderBottom: `1px solid ${T.border}`,
          }}
        >
          <span
            style={{
              display: "inline-block", width: 8, height: 8, borderRadius: "50%",
              background: spaceFitColorFor(loc), flexShrink: 0,
            }}
          />
          <span style={{ flex: 1, color: T.dark, fontSize: T.fsSm, minWidth: 0 }}>
            {loc.market}{loc.locationAlias && <span style={{ color: T.mid }}> · {loc.locationAlias}</span>}
          </span>
          <button
            type="button"
            onClick={() => onSelectLocation?.(loc.id)}
            data-testid={`colocated-popup-view-${loc.id}`}
            data-location-id={loc.id}
            aria-pressed={selectedId === loc.id}
            style={{
              padding: "3px 8px", background: selectedId === loc.id ? T.blue : T.hdrMid, color: T.white, border: "none",
              borderRadius: 4, fontSize: T.fsXs, fontWeight: T.fwSemibold,
              cursor: "pointer", whiteSpace: "nowrap",
            }}
          >
            View Details →
          </button>
        </div>
      ))}
    </div>
  );
}

function SidebarRow({ location, onClick, onNavigate, selected }) {
  const [hover, setHover] = useState(false);
  const [arrowHover, setArrowHover] = useState(false);
  const stage = STAGES[location.stage] || { label: location.stage };
  const sqftRaw  = getDisplayedSqft(location, null);
  const sqftStr  = sqftRaw != null
    ? fmtSqft(sqftRaw)
    : (isServicedNoArea(location) ? "n/a" : "");
  const seatsStr = location.installedCapacity
    ? `${Math.round(location.installedCapacity)} seats`
    : "";
  const meta = [stage.label, sqftStr, seatsStr].filter(Boolean).join(" · ");
  const dotColor = spaceFitColorFor(location);
  const stageNote = SPACE_FIT_STAGE_NOTE[location.stage] ?? null;
  return (
    <div
      data-testid={`map-sidebar-row-${location.id}`}
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      onClick={() => onClick(location.id)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick(location.id);
        }
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "flex",
        alignItems: "center",
        padding: "9px 14px",
        borderBottom: `1px solid ${T.border}`,
        cursor: "pointer",
        background: selected ? T.rowActive : hover ? T.bg : T.card,
        transition: "background 0.1s",
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: T.fsBase, fontWeight: T.fwSemibold, color: T.dark, marginBottom: 3 }}>
          {location.market}
          {location.locationAlias && (
            <span style={{ fontWeight: T.fwRegular, color: T.mid }}> · {location.locationAlias}</span>
          )}
        </div>
        <div style={{ fontSize: T.fsXs, color: T.mid }}>
          <span
            data-testid={`map-sidebar-health-dot-${location.id}`}
            style={{
              display: "inline-block", width: 7, height: 7, borderRadius: "50%",
              background: dotColor,
              marginRight: 6, verticalAlign: "middle",
            }}
          />
          {meta}
          {stageNote && (
            <span
              data-testid={`map-sidebar-space-fit-note-${location.id}`}
              style={{ fontSize: T.fsXxs5, color: "var(--v2-light)", marginLeft: 4 }}
            >
              {stageNote}
            </span>
          )}
        </div>
      </div>
      {/* Navigate arrow — same styling as LocationCard list view arrow.
          stopPropagation prevents the row's map-focus onClick from firing. */}
      <div
        data-testid={`map-sidebar-arrow-${location.id}`}
        onClick={(e) => { e.stopPropagation(); onNavigate && onNavigate(location.id); }}
        onMouseEnter={() => setArrowHover(true)}
        onMouseLeave={() => setArrowHover(false)}
        style={{
          flex: "0 0 16px",
          color: arrowHover ? T.mid : T.light,
          fontSize: T.fsBase,
          opacity: arrowHover ? 1 : 0.6,
          cursor: "pointer",
          transition: "color 0.1s, opacity 0.1s",
        }}
      >
        →
      </div>
    </div>
  );
}

function MapSidebar({ mapped, onClick, onNavigate, selectedId }) {
  const scrollRef = useRef(null);
  const contentRef = useRef(null);
  const [remaining, setRemaining] = useState(0);

  const measure = useCallback(() => {
    const viewport = scrollRef.current;
    const content = contentRef.current;
    if (!viewport || !content) return;
    const bottom = viewport.scrollTop + viewport.clientHeight;
    const count = viewport.scrollHeight - bottom > 1
      ? Array.from(content.children).filter((row) => row.offsetTop + row.offsetHeight > bottom + 1).length
      : 0;
    setRemaining((previous) => previous === count ? previous : count);
  }, []);

  useLayoutEffect(() => {
    const viewport = scrollRef.current;
    const content = contentRef.current;
    if (!viewport || !content) return undefined;
    measure();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    observer?.observe(viewport);
    observer?.observe(content);
    return () => observer?.disconnect();
  }, [mapped, measure]);

  return (
    <div
      data-testid="locations-map-sidebar"
      style={{
        width: 240, flexShrink: 0, minHeight: 0, display: "flex", flexDirection: "column",
        background: T.card, borderRight: `1px solid ${T.border}`,
      }}
    >
      <div style={{ position: "relative", flex: 1, minHeight: 0 }}>
        <div
          ref={scrollRef}
          data-testid="locations-map-sidebar-scroll"
          onScroll={measure}
          style={{
            height: "100%", overflowY: "auto",
            scrollbarWidth: "thin", scrollbarColor: `${T.borderDark} ${T.card}`,
          }}
        >
          <div ref={contentRef}>
            {mapped.map((loc) => (
              <SidebarRow key={loc.id} location={loc} selected={selectedId === loc.id} onClick={onClick} onNavigate={onNavigate} />
            ))}
          </div>
        </div>
        {remaining > 0 && (
          <div
            data-testid="map-sidebar-fade"
            aria-hidden="true"
            style={{
              position: "absolute", bottom: 0, left: 0, right: 0, height: 28,
              background: `linear-gradient(to bottom, transparent, ${T.card})`,
              pointerEvents: "none",
            }}
          />
        )}
      </div>
      {remaining > 0 && (
        <div
          data-testid="map-sidebar-more"
          aria-live="polite"
          style={{
            flexShrink: 0, textAlign: "center", padding: "4px 0 6px",
            fontSize: T.fsXs, color: T.mid, pointerEvents: "none",
          }}
        >
          {remaining} more ↓
        </div>
      )}
    </div>
  );
}

// ── Top-level component ───────────────────────────────────────────────────────

export function LocationsMap({
  onSelectLocation,
  selectedId = null,
  locations: locationsProp,
  loading: loadingProp,
  error: errorProp,
  emptyState,
}) {
  const [internalLocations, setInternalLocations] = useState([]);
  const [internalLoading, setInternalLoading]     = useState(true);
  const [internalError, setInternalError]         = useState(null);
  const mapRef = useRef(null);

  const usingProps = locationsProp !== undefined;

  useEffect(() => {
    if (usingProps) return;
    let cancelled = false;
    setInternalLoading(true);
    getLocations()
      .then((list) => {
        if (cancelled) return;
        setInternalLocations(list);
        setInternalError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        log.error("LocationsMap: getLocations failed", { name: err?.name, msg: err?.message });
        setInternalError(err);
      })
      .finally(() => {
        if (!cancelled) setInternalLoading(false);
      });
    return () => { cancelled = true; };
  }, [usingProps]);

  const sourceList = usingProps ? (locationsProp || []) : internalLocations;
  const loading    = usingProps ? !!loadingProp : internalLoading;
  const error      = usingProps ? errorProp     : internalError;

  // P2 (2026-05-12): when mounted by PortfolioView, the parent now
  // owns sort order (Sort dropdown — smart/size/capacity)
  // AND has already called rankSites upstream. Re-ranking here would
  // (a) overwrite the parent's chosen sort order on the sidebar, and
  // (b) re-number ranks on filtered subsets (the same regression class
  // documented in src/utils/sortLocations.js). Trust parent order when
  // `usingProps`; only rank in standalone-mount mode where this
  // component fetched its own data.
  const sorted = useMemo(
    () => (usingProps ? sourceList : rankSites(sourceList)),
    [usingProps, sourceList],
  );

  const mapped = useMemo(() => sorted.filter(hasCoords), [sorted]);
  const mapFeatures = useMemo(
    () => Array.from(groupByCoords(mapped).entries()).map(([coordinateKey, locations]) => ({
      key: `coords:${coordinateKey}`,
      coordinates: [effectiveLat(locations[0]), effectiveLng(locations[0])],
      locations,
      color: locations.length > 1
        ? worstClusterColor(locations.map((location) => ({
            ...location,
            spaceFitVerdict: computedSpaceFitVerdict(location),
          })))
        : spaceFitColorFor(locations[0]),
      severity: severityForLocations(locations),
    })),
    [mapped],
  );

  const handleSidebarClick = (id) => {
    const loc = mapped.find((l) => l.id === id);
    if (!loc) return;
    mapRef.current?.flyTo({
      center: [effectiveLng(loc), effectiveLat(loc)],
      zoom: 11,
      duration: 600,
    });
    onSelectLocation?.(id);
  };

  // NB: do NOT extract this wrapper into a `Wrapper` component defined
  // inside LocationsMap. React would treat that as a new component
  // identity on every render, unmounting and remounting the entire map
  // subtree. Keep the wrapping inline so the same DOM nodes are preserved
  // across parent re-renders.

  return (
    <div>
      {!usingProps && <ErrorBanner error={error} onDismiss={() => setInternalError(null)} />}
      {usingProps   && <ErrorBanner error={error} />}

      {!loading && !error && sourceList.length === 0 && (
        emptyState ?? <div style={S.emptyState}>No locations have coordinates yet.</div>
      )}

      {!loading && !error && sourceList.length > 0 && mapped.length === 0 && (
        <div style={S.emptyState}>No locations have coordinates yet.</div>
      )}

      {!loading && !error && mapped.length > 0 && (
        <>
          {/* Hidden-count notice removed: OfficesPage now renders it via
              StatusBand (OFFICES-MAP-HIDDEN-COUNT-INTO-STATUSBAND). */}
          <div
            data-testid="locations-map-shell"
            style={{
              display: "flex", height: 600, borderRadius: 8, overflow: "hidden",
              border: `1px solid ${T.border}`, boxShadow: T.cardShadow,
              isolation: "isolate",
            }}
          >
            <MapSidebar mapped={mapped} selectedId={selectedId} onClick={handleSidebarClick} onNavigate={onSelectLocation} />

            <div style={{ flex: 1, position: "relative" }} data-testid="locations-map-canvas">
              <MapSpaceFitLegend />
              <OfficeMapCanvas
                features={mapFeatures}
                mapRef={mapRef}
                center={DEFAULT_CENTER}
                zoom={DEFAULT_ZOOM}
                cluster
                testId="locations-map-container"
                ariaLabel="Offices map"
                onSelectLocation={onSelectLocation}
                selectedId={selectedId}
                selectSingleton
                renderPopup={(feature) => feature.locations.length > 1
                  ? <ColocatedPopup locations={feature.locations} selectedId={selectedId} onSelectLocation={onSelectLocation} />
                  : <LocationPopup
                      location={feature.locations[0]}
                      markerColor={feature.color}
                      onSelectLocation={onSelectLocation}
                    />}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
